import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { expenseApi, employeeApi } from '../services/api';
import { Expense, ExpenseCreateRequest, ExpenseUpdateRequest, ExpenseCategory, RecurrenceFrequency } from '../types/expense';
import { DateInputField } from '../components/DateInputField';
import { formatDate, formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { enumLabel } from '../i18n/translate';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ExpenseManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [expenseType, setExpenseType] = useState<'one-time' | 'recurring' | 'salary'>('one-time');
  const [formData, setFormData] = useState<ExpenseCreateRequest>({
    category: 'OTHER',
    expenseDate: dayjs().format('YYYY-MM-DD'),
    amount: 0,
    description: '',
    vendorName: '',
    paymentMethod: '',
    paymentReference: '',
    notes: '',
    isRecurring: false,
  });

  const { data: expenses = [], isLoading } = useQuery('expenses', expenseApi.getAll);
  const { data: recurringExpenses = [] } = useQuery('recurringExpenses', expenseApi.getRecurring);
  const { data: reminders = [] } = useQuery('expenseReminders', expenseApi.getReminders);
  const { data: employees = [] } = useQuery('employees', employeeApi.getAll);
  const activeEmployees = employees.filter((employee) => employee.status === 'ACTIVE');

  const createMutation = useMutation(expenseApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('expenses');
      queryClient.invalidateQueries('recurringExpenses');
      queryClient.invalidateQueries('upcomingExpenses');
      queryClient.invalidateQueries('expenseReminders');
      toast.success(t('expenses.messages.created'));
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('expenses.failedCreate'));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: ExpenseUpdateRequest }) => expenseApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('expenses');
        queryClient.invalidateQueries('recurringExpenses');
        queryClient.invalidateQueries('upcomingExpenses');
        queryClient.invalidateQueries('expenseReminders');
        toast.success(t('expenses.messages.updated'));
        handleCloseDialog();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('expenses.failedUpdate'));
      },
    }
  );

  const deleteMutation = useMutation(expenseApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('expenses');
      queryClient.invalidateQueries('recurringExpenses');
      queryClient.invalidateQueries('upcomingExpenses');
      toast.success(t('expenses.messages.deleted'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('expenses.failedDelete'));
    },
  });

  const handleOpenDialog = (expense?: Expense, type: 'one-time' | 'recurring' | 'salary' = 'one-time') => {
    setExpenseType(type);
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        category: expense.category,
        expenseDate: expense.expenseDate,
        amount: expense.amount,
        description: expense.description || '',
        vendorName: expense.vendorName || '',
        paymentMethod: expense.paymentMethod || '',
        paymentReference: expense.paymentReference || '',
        notes: expense.notes || '',
        isRecurring: expense.isRecurring || false,
        recurrenceFrequency: expense.recurrenceFrequency,
        recurrenceStartDate: expense.recurrenceStartDate,
        recurrenceEndDate: expense.recurrenceEndDate,
        reminderDaysBefore: expense.reminderDaysBefore,
        employeeId: expense.employeeId,
      });
    } else {
      setSelectedExpense(null);
      if (type === 'salary') {
        setFormData({
          category: 'SALARY',
          expenseDate: dayjs().format('YYYY-MM-DD'),
          amount: 0,
          description: '',
          vendorName: '',
          paymentMethod: '',
          paymentReference: '',
          notes: '',
          isRecurring: true,
          recurrenceFrequency: 'MONTHLY',
          recurrenceStartDate: dayjs().format('YYYY-MM-DD'),
          reminderDaysBefore: 3,
        });
      } else if (type === 'recurring') {
        setFormData({
          category: 'OTHER',
          expenseDate: dayjs().format('YYYY-MM-DD'),
          amount: 0,
          description: '',
          vendorName: '',
          paymentMethod: '',
          paymentReference: '',
          notes: '',
          isRecurring: true,
          recurrenceFrequency: 'MONTHLY',
          recurrenceStartDate: dayjs().format('YYYY-MM-DD'),
          reminderDaysBefore: 3,
        });
      } else {
        setFormData({
          category: 'OTHER',
          expenseDate: dayjs().format('YYYY-MM-DD'),
          amount: 0,
          description: '',
          vendorName: '',
          paymentMethod: '',
          paymentReference: '',
          notes: '',
          isRecurring: false,
        });
      }
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExpense(null);
  };

  const handleSave = () => {
    if (!formData.category || !formData.expenseDate || !formData.amount || formData.amount <= 0) {
      toast.error(t('expenses.validation.required'));
      return;
    }

    if (selectedExpense) {
      updateMutation.mutate({ id: selectedExpense.id.toString(), data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (expense: Expense) => {
    if (window.confirm(t('expenses.deleteConfirm', { number: expense.expenseNumber }))) {
      deleteMutation.mutate(expense.id.toString());
    }
  };

  const handleMarkReminderSent = (expenseId: number) => {
    expenseApi.markReminderSent(expenseId.toString()).then(() => {
      queryClient.invalidateQueries('expenseReminders');
      toast.success(t('expenses.messages.reminderSent'));
    });
  };

  const oneTimeExpenses = expenses.filter((e) => !e.isRecurring);
  const salaryExpenses = expenses.filter((e) => e.category === 'SALARY' && e.isRecurring);

  const getCategoryColor = (category: ExpenseCategory) => {
    switch (category) {
      case 'SALARY':
        return 'primary';
      case 'INVOICE':
        return 'info';
      case 'UTILITIES':
        return 'warning';
      case 'RENT':
        return 'error';
      default:
        return 'default';
    }
  };

  const expenseCategories: ExpenseCategory[] = [
    'SALARY',
    'INVOICE',
    'UTILITIES',
    'RENT',
    'MAINTENANCE',
    'SUPPLIES',
    'MARKETING',
    'INSURANCE',
    'TAXES',
    'OTHER',
  ];

  const recurrenceFrequencies: RecurrenceFrequency[] = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('expenses.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('expenses.subtitle')}
      </Typography>

      {/* Reminders Alert */}
      {reminders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              <Typography>
                {t('expenses.reminderAlert', { count: reminders.length, plural: reminders.length > 1 ? 's' : '' })}
              </Typography>
            </Box>
            <Button size="small" onClick={() => setSelectedTab(1)}>{t('common.viewDetails')}</Button>
          </Box>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog(undefined, 'one-time')}
        >
          {t('expenses.addOneTime')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<ScheduleIcon />}
          onClick={() => handleOpenDialog(undefined, 'recurring')}
        >
          {t('expenses.addRecurring')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PeopleIcon />}
          onClick={() => handleOpenDialog(undefined, 'salary')}
        >
          {t('expenses.addSalary')}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={t('expenses.oneTimeTab', { count: oneTimeExpenses.length })} />
          <Tab
            label={
              <Badge badgeContent={reminders.length} color="error">
                {t('expenses.recurringTab', { count: recurringExpenses.length })}
              </Badge>
            }
          />
          <Tab label={t('expenses.employeeSalariesTab', { count: salaryExpenses.length })} />
        </Tabs>
      </Box>

      {/* One-Time Expenses Tab */}
      <TabPanel value={selectedTab} index={0}>
        {oneTimeExpenses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('expenses.emptyOneTime')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('expenses.emptyOneTimeHint')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {oneTimeExpenses.map((expense) => (
              <Grid item xs={12} key={expense.id}>
                <Card>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <ReceiptIcon color="action" />
                          <Typography variant="h6">{expense.expenseNumber}</Typography>
                          <Chip
                            label={enumLabel(t, 'expenseCategories', expense.category)}
                            color={getCategoryColor(expense.category) as any}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {formatDate(expense.expenseDate)}
                        </Typography>
                        {expense.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {expense.description}
                          </Typography>
                        )}
                        {expense.vendorName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('expenses.vendorLabel')} {expense.vendorName}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h5" color="error" sx={{ mb: 2 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenDialog(expense, 'one-time')}
                            >{t('common.edit')}</Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(expense)}
                            >{t('common.delete')}</Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Recurring Expenses Tab */}
      <TabPanel value={selectedTab} index={1}>
        {recurringExpenses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('expenses.emptyRecurring')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('expenses.emptyRecurringHint')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {recurringExpenses.map((expense) => (
              <Grid item xs={12} key={expense.id}>
                <Card sx={{ border: expense.isDueSoon || expense.isOverdue ? 2 : 0, borderColor: expense.isOverdue ? 'error.main' : 'warning.main' }}>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <ScheduleIcon color="action" />
                          <Typography variant="h6">{expense.expenseNumber}</Typography>
                          <Chip
                            label={enumLabel(t, 'expenseCategories', expense.category)}
                            color={getCategoryColor(expense.category) as any}
                            size="small"
                          />
                          <Chip
                            label={enumLabel(t, 'recurrenceFrequencies', expense.recurrenceFrequency)}
                            size="small"
                            variant="outlined"
                          />
                          {expense.isOverdue && (
                            <Chip label={t('common.overdue')} color="error" size="small" />
                          )}
                          {expense.isDueSoon && !expense.isOverdue && (
                            <Chip label={t('common.dueSoon')} color="warning" size="small" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('expenses.nextDueLabel')} {expense.nextDueDate ? formatDate(expense.nextDueDate) : t('common.unavailable')}
                        </Typography>
                        {expense.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {expense.description}
                          </Typography>
                        )}
                        {expense.employeeName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('expenses.employeeLabel')} {expense.employeeName}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h5" color="error" sx={{ mb: 2 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenDialog(expense, 'recurring')}
                            >{t('common.edit')}</Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(expense)}
                            >{t('common.delete')}</Button>
                            {expense.isDueSoon && (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                startIcon={<NotificationsIcon />}
                                onClick={() => handleMarkReminderSent(expense.id)}
                              >
                                {t('expenses.markReminderSent')}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Employee Salaries Tab */}
      <TabPanel value={selectedTab} index={2}>
        {salaryExpenses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('expenses.emptySalaries')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('expenses.emptySalariesHint')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {salaryExpenses.map((expense) => (
              <Grid item xs={12} key={expense.id}>
                <Card>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PeopleIcon color="action" />
                          <Typography variant="h6">
                            {expense.employeeName || expense.vendorName || t('expenses.employeeSalary')}
                          </Typography>
                          <Chip label={t('expenses.monthlySalary')} color="primary" size="small" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('expenses.nextPaymentLabel')} {expense.nextDueDate ? formatDate(expense.nextDueDate) : t('common.unavailable')}
                        </Typography>
                        {expense.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {expense.description}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenDialog(expense, 'salary')}
                            >{t('common.edit')}</Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(expense)}
                            >{t('common.delete')}</Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedExpense ? t('expenses.editExpense') : expenseType === 'salary' ? t('expenses.addSalary') : expenseType === 'recurring' ? t('expenses.addRecurring') : t('expenses.addOneTime')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {expenseType === 'salary' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('roles.employee')}</InputLabel>
                  <Select
                    value={formData.employeeId || ''}
                    label={t('roles.employee')}
                    onChange={(e) => {
                      const employeeId = e.target.value as number;
                      const employee = activeEmployees.find((e) => e.id === employeeId);
                      setFormData({
                        ...formData,
                        employeeId,
                        vendorName: employee ? `${employee.firstName} ${employee.lastName}` : '',
                        description: employee ? t('expenses.salaryDescription', { employee: `${employee.firstName} ${employee.lastName}` }) : '',
                      });
                    }}
                    disabled={!!selectedExpense}
                  >
                    {activeEmployees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.fullName}{emp.employeeCode ? ` (${emp.employeeCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('fields.category')}</InputLabel>
                <Select
                  value={formData.category}
                  label={t('fields.category')}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
                  disabled={expenseType === 'salary'}
                >
                  {expenseCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {enumLabel(t, 'expenseCategories', cat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('fields.amount')}
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {(expenseType === 'recurring' || expenseType === 'salary') && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isRecurring || false}
                        onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                        disabled={expenseType === 'salary'}
                      />
                    }
                    label={t('expenses.recurringExpense')}
                  />
                </Grid>

                {formData.isRecurring && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('fields.frequency')}</InputLabel>
                        <Select
                          value={formData.recurrenceFrequency || 'MONTHLY'}
                          label={t('fields.frequency')}
                          onChange={(e) => setFormData({ ...formData, recurrenceFrequency: e.target.value as RecurrenceFrequency })}
                        >
                          {recurrenceFrequencies.map((freq) => (
                            <MenuItem key={freq} value={freq}>
                              {enumLabel(t, 'recurrenceFrequencies', freq)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateInputField
                        fullWidth
                        label={t('fields.startDate')}
                        value={formData.recurrenceStartDate || formData.expenseDate}
                        onChange={(recurrenceStartDate) =>
                          setFormData({ ...formData, recurrenceStartDate })
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateInputField
                        fullWidth
                        label={t('fields.recurrenceEndDate')}
                        value={formData.recurrenceEndDate || ''}
                        onChange={(recurrenceEndDate) =>
                          setFormData({ ...formData, recurrenceEndDate: recurrenceEndDate || undefined })
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('fields.reminderDaysBefore')}
                        type="number"
                        value={formData.reminderDaysBefore || 3}
                        onChange={(e) => setFormData({ ...formData, reminderDaysBefore: parseInt(e.target.value) || 3 })}
                        inputProps={{ min: 0, max: 30 }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {expenseType !== 'salary' && (
              <>
                <Grid item xs={12} sm={6}>
                  <DateInputField
                    fullWidth
                    label={t('fields.expenseDate')}
                    value={formData.expenseDate}
                    onChange={(expenseDate) => setFormData({ ...formData, expenseDate })}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('fields.vendorName')}
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('fields.description')}
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('fields.paymentMethod')}
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('fields.paymentReference')}
                value={formData.paymentReference}
                onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('fields.notes')}
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('common.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
